import React, { useState, useMemo } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import dayjs from "dayjs";


import { getS3PublicUrl, getS3PublicUrlCandidates } from "../../../../utils/s3";
import storeDefault from "../../../../Assets/Icons/storeDefault.svg";

const OfferDetails = ({ offerData, merchantData, firstLocation }) => {
  const handleS3ImageError = (event, rawUrl) => {
    const candidates = getS3PublicUrlCandidates(rawUrl);
    const currentIndex = Number(event.currentTarget.dataset.fallbackIndex || 0);
    const nextIndex = currentIndex + 1;
    if (nextIndex < candidates.length) {
      event.currentTarget.dataset.fallbackIndex = String(nextIndex);
      event.currentTarget.src = candidates[nextIndex];
      return;
    }
    event.currentTarget.style.display = "none";
  };

  const [showAllRules, setShowAllRules] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Combine thumbnail and preview images for the carousel
  const allImages = useMemo(() => {
    const images = [];
    if (offerData?.thumbnailImage) images.push(offerData.thumbnailImage);
    if (Array.isArray(offerData?.previewImages)) {
      offerData.previewImages.forEach(img => {
        if (img && img !== offerData.thumbnailImage) images.push(img);
      });
    }
    return images;
  }, [offerData?.thumbnailImage, offerData?.previewImages]);

  var settings = {
    dots: allImages.length > 1,
    arrows: false,
    infinite: true,
    autoplay: true,
    speed: 800,
    slidesToShow: 1,
    slidesToScroll: 1,
    cssEase: "cubic-bezier(0.16, 1, 0.3, 1)",
    autoplaySpeed: 4000,
    beforeChange: (oldIndex, newIndex) => setCurrentSlide(newIndex),
  };

  // Compute consistent discount label and type tag
  const discountText = (() => {
    if (offerData?.discountTitle) return offerData.discountTitle;
    switch (offerData?.offerDiscountType) {
      case "FREE_OFFER":
        return "Free Offer";
      case "DISCOUNTED_PERCENT":
        return `Discounted Offer ${offerData?.discountRange || 0}%`;
      case "FIXED_DOLLAR":
        return `$${offerData?.fixedDollarAmount || 0} Fixed Price`;
      case "BUY_ONE_GET_ONE_FREE":
        return "Buy 1 Get 1 Free";
      case "BUNDLE_OFFER":
        return "Bundle Offer";
      default:
        return offerData?.discountRange !== undefined && offerData?.discountRange !== null
          ? `Discounted Offer ${offerData?.discountRange}%`
          : null;
    }
  })();

  const offerTypeTag = offerData?.offerType === 'INSTORE_SWOODLE_OFFER'
    ? 'In-store Offer'
    : (offerData?.offerType === 'ONLINE_OFFER' ? 'Online Offer' : '');

  const location = firstLocation || merchantData?.location;
  const merchant = offerData?.merchant || merchantData?.location?.merchant

  const merchantName = merchant?.businessName || location?.name || merchant?.user?.name || "";
  const merchantLogo = location?.logo || merchant?.logo;

  const rating = merchantData?.location?.googlePlaceId?.rating;

  const redeemButtonLabel = offerData?.onlineOption === 'ONLINE' ? 'Visit Website' : 'Redeem';

  // Helper for Date Formatting
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Updated Helper for Days Formatting (Weekdays + Consecutive Days)
  const formatRedemptionDays = (daysArray) => {
    if (!Array.isArray(daysArray) || daysArray.length === 0) return "";

    const shortDayMap = {
      Monday: "Mon",
      Tuesday: "Tue",
      Wednesday: "Wed",
      Thursday: "Thu",
      Friday: "Fri",
      Saturday: "Sat",
      Sunday: "Sun",
    };
    const weekdayOrder = {
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
      Sunday: 7,
    };

    const dayList = Array.from(
      new Set(
        daysArray
          .map((day) =>
            typeof day === "string"
              ? day.charAt(0).toUpperCase() + day.slice(1).toLowerCase()
              : ""
          )
          .filter(Boolean)
      )
    ).sort((a, b) => (weekdayOrder[a] || 99) - (weekdayOrder[b] || 99));

    if (dayList.length === 0) return "";

    const selectedSet = new Set(dayList);
    const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

    // 1. Check if EXACTLY Monday to Friday are selected
    const isWeekdaysOnly =
      selectedSet.size === 5 && weekdays.every((day) => selectedSet.has(day));

    if (isWeekdaysOnly) {
      return "On weekdays";
    }

    // 2. All 7 Days
    if (dayList.length === 7) return "On every day";

    const shortDays = dayList.map((d) => shortDayMap[d] || d);

    // 3. Check if days are consecutive (e.g. Fri, Sat, Sun -> "On every Fri to Sun")
    let isConsecutive = true;
    for (let i = 0; i < dayList.length - 1; i++) {
      if (weekdayOrder[dayList[i + 1]] - weekdayOrder[dayList[i]] !== 1) {
        isConsecutive = false;
        break;
      }
    }

    if (isConsecutive && shortDays.length > 2) {
      return `On every ${shortDays[0]} to ${shortDays[shortDays.length - 1]}`;
    }

    if (shortDays.length === 1) return `On every ${shortDays[0]}`;
    if (shortDays.length === 2) return `On every ${shortDays[0]} & ${shortDays[1]}`;

    return `On every ${shortDays.slice(0, -1).join(", ")} & ${shortDays[shortDays.length - 1]
      }`;
  };

  // 2. Main Message Helper (Exact Mobile Logic)
  const getRedemptionDetails = ({ necessaryRule, frequency, redemptionDays, endDate }) => {
    const ruleMessages = {
      REDEEM_ONCE: {
        ONCE: "Redeem once",
        DAILY: "Redeem once daily",
        IN_A_WEEK: "Redeem once a week",
        EVERY_30_DAYS: "Redeem once in 30 days",
        EVERY_90_DAYS: "Redeem once in 90 days",
        EVERY_120_DAYS: "Redeem once in 120 days",
        ONCE_IN_12_MONTHS: "Redeem once in 12 months",
        CONTINUALLY: "Redeem anytime, continually",
      },
      OFFER_AVAILABLE_THROUGHOUT_YEAR: {
        ONCE: "Redeem once per year",
        ONCE_DAILY: "Redeem once Daily, all year",
        IN_A_WEEK: "Redeem once a week, all year",
        EVERY_30_DAYS: "Redeem once a month, all year",
        EVERY_90_DAYS: "Redeem once in 90 days, all year",
        EVERY_120_DAYS: "Redeem once in 120 days, all year",
        ONCE_IN_12_MONTHS: "Redeem one time this year",
        CONTINUALLY: "Redeem anytime all year",
      },
      CHOOSE_DATE_RANGE: {
        ONCE: "Redeem once on selected days",
        DAILY: "Redeem once on each selected day",
        IN_A_WEEK: "Redeem once a week",
        EVERY_30_DAYS: "Redeem once every 30 days on selected days",
        EVERY_90_DAYS: "Redeem once in 90 days on selected days",
        EVERY_120_DAYS: "Redeem once in 120 days on selected days",
        ONCE_IN_12_MONTHS: "Redeem one time this year",
        CONTINUALLY: "Redeem anytime on selected days",
      },
    };

    // Title Extract
    const title = ruleMessages[necessaryRule]?.[frequency || ""] || "Redeem Offer";

    // Days Text & End Date Formatting
    const daysText = formatRedemptionDays(redemptionDays);
    const formattedEndDate = endDate ? formatDate(endDate) : "";

    // Combine Subtitle Text
    let subtitle = "";
    if (daysText && formattedEndDate) {
      subtitle = `${daysText} Until ${formattedEndDate}`;
    } else if (daysText) {
      subtitle = daysText;
    } else if (formattedEndDate) {
      subtitle = `Until ${formattedEndDate}`;
    }

    return { title, subtitle };
  };

  // 1. High-Quality Inline Icons to fix blurriness
  const NewDiscountIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 15L15 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 10C9.55228 10 10 9.55228 10 9C10 8.44772 9.55228 8 9 8C8.44772 8 8 8.44772 8 9C8 9.55228 8.44772 10 9 10Z" fill="white" />
      <path d="M15 16C15.5523 16 16 15.5523 16 15C16 14.4477 15.5523 14 15 14C14.4477 14 14 14.4477 14 15C14 15.5523 14.4477 16 15 16Z" fill="white" />
    </svg>
  );

  const NewInStoreIcon = () => (
    <svg width="14" height="14" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6.74981 10.5V6.75C6.74981 6.65054 6.78932 6.55516 6.85965 6.48484C6.92997 6.41451 7.02536 6.375 7.12481 6.375H8.62481C8.72427 6.375 8.81965 6.41451 8.88998 6.48484C8.9603 6.55516 8.99981 6.65054 8.99981 6.75V10.5M6.74981 10.5H1.17981M6.74981 10.5H8.99981M8.99981 10.5H10.8198M10.1248 10.5V4.6745M10.1248 4.6745C9.82486 4.84755 9.47428 4.91152 9.13253 4.85557C8.79079 4.79961 8.47892 4.62717 8.24981 4.3675C7.97481 4.6785 7.57281 4.875 7.12481 4.875C6.91195 4.87521 6.70149 4.83 6.50749 4.7424C6.3135 4.65479 6.14042 4.52681 5.99981 4.367C5.72481 4.6785 5.32281 4.875 4.87481 4.875C4.66195 4.87521 4.45149 4.83 4.25749 4.7424C4.0635 4.65479 3.89042 4.52681 3.74981 4.367C3.52077 4.62676 3.20893 4.79929 2.86718 4.85534C2.52544 4.91138 2.17482 4.84749 1.87481 4.6745M10.1248 4.6745C10.3241 4.55945 10.4943 4.4001 10.6221 4.20876C10.75 4.01742 10.8321 3.79923 10.8621 3.57108C10.8921 3.34292 10.8692 3.11092 10.7952 2.89302C10.7212 2.67513 10.5981 2.47718 10.4353 2.3145L9.84031 1.72C9.69979 1.57931 9.50916 1.50018 9.31031 1.5H2.68881C2.49004 1.50004 2.29941 1.57899 2.15881 1.7195L1.56431 2.3145C1.40193 2.47737 1.27912 2.67535 1.20535 2.89319C1.13157 3.11102 1.10881 3.34289 1.13881 3.57091C1.16881 3.79893 1.25077 4.01702 1.37838 4.20836C1.50599 4.3997 1.67583 4.55918 1.87481 4.6745M1.87481 10.5V4.6745M3.37481 9H5.24981C5.34927 9 5.44465 8.96049 5.51498 8.89017C5.5853 8.81984 5.62481 8.72446 5.62481 8.625V6.75C5.62481 6.65054 5.5853 6.55516 5.51498 6.48484C5.44465 6.41451 5.34927 6.375 5.24981 6.375H3.37481C3.27536 6.375 3.17997 6.41451 3.10965 6.48484C3.03932 6.55516 2.99981 6.65054 2.99981 6.75V8.625C2.99981 8.832 3.16781 9 3.37481 9Z" stroke="white" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const NewWebIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="1.5" />
      <path d="M2.06 12H21.94" stroke="white" strokeWidth="1.5" />
      <path d="M12 2.06C14.5 4.5 15.5 8.25 15.5 12C15.5 15.75 14.5 19.5 12 21.94C9.5 19.5 8.5 15.75 8.5 12C8.5 8.25 9.5 4.5 12 2.06Z" stroke="white" strokeWidth="1.5" />
    </svg>
  );

  //Discount text and offer type tag are computed above for consistency across the component
  const formatDiscountText = (text) => {
    if (!text) return null;

    const cleanText = text.trim();

    // 1. "Discounted Offer 25%" -> "25% OFF"
    if (cleanText.toLowerCase().includes("discounted offer")) {
      const percentage = cleanText.match(/\d+%/)?.[0] || "";
      return `${percentage} OFF`.toUpperCase();
    }

    // 2. "$20 Fixed Price" -> "$20 OFF"
    if (cleanText.toLowerCase().includes("fixed price")) {
      const amount = cleanText.match(/\$\d+(\.\d+)?/)?.[0] || "";
      return `${amount} OFF`.toUpperCase();
    }

    // 3. "Free Offer" -> "FREE"
    if (cleanText.toLowerCase() === "free offer") {
      return "FREE";
    }

    // 4. "Buy 1 Get 1 Free" -> "BUY 1 GET 1"
    if (cleanText.toLowerCase().includes("buy 1 get 1")) {
      return "BUY 1 GET 1";
    }

    // 5. "Bundle Offer" -> "BUNDLE"
    if (cleanText.toLowerCase().includes("bundle offer")) {
      return "BUNDLE";
    }

    // Generic Fallback (agar koi new pattern aye)
    return cleanText.toUpperCase();
  };

  return (
    <div className="flex flex-col bg-white w-full max-w-[370px] min-h-[680px] border border-gray-100 shadow-xl relative mx-auto overflow-hidden">

      {/* Ticket-shaped image container */}
      <div className="ticket-image-multi-cutout w-full min-h-[210px] mb-0 mx-auto mt-0 relative overflow-hidden bg-black">

        {/* Top Actions: Back Button & Heart Icon */}
        <div className="absolute top-3 left-3 right-3 z-20 flex justify-between items-center pointer-events-none">
          <button
            onClick={() => window.history.back()}
            className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-md pointer-events-auto hover:bg-gray-100 transition-all"
          >
            <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button className="w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-md pointer-events-auto hover:bg-gray-100 transition-all">
            <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </button>
        </div>

        {/* Filter Cover Image & Display UI */}
        {(() => {
          const coverUrl = offerData?.coverImage || offerData?.thumbnailImage;
          const nonCoverImages = (allImages || []).filter(imgUrl => imgUrl !== coverUrl);
          const formattedBadgeText = formatDiscountText(discountText);

          if (nonCoverImages.length > 1) {
            return (
              <Slider className="w-full h-full review-details-carousel" {...settings}>
                {nonCoverImages.map((url, idx) => {
                  const resolved = getS3PublicUrl(url);
                  if (!url) return <div key={idx} className="w-full h-[210px] bg-black" />;

                  return (
                    <div key={idx} className="w-full h-[210px] outline-none relative bg-black">
                      <img
                        src={resolved}
                        className="w-full h-full object-contain"
                        style={{ background: 'transparent', borderRadius: 0 }}
                        alt=""
                        data-fallback-index="0"
                        onError={(event) => handleS3ImageError(event, url)}
                      />
                      {formattedBadgeText && (
                        <span className="absolute bottom-3 left-3 z-10 bg-[#00ADEF] text-white text-[12px] font-bold rounded-xl px-3 py-1.5 uppercase tracking-wide shadow-sm">
                          {formattedBadgeText}
                        </span>
                      )}
                    </div>
                  );
                })}
              </Slider>
            );
          }

          const displayUrl = nonCoverImages[0] || coverUrl;
          const resolved = getS3PublicUrl(displayUrl);

          if (!displayUrl) return <div className="w-full h-full bg-black" />;

          return (
            <div className="w-full h-[210px] relative bg-black">
              <img
                src={resolved}
                className="w-full h-full object-contain"
                style={{ background: 'transparent', borderRadius: 0 }}
                alt=""
                data-fallback-index="0"
                onError={(event) => handleS3ImageError(event, displayUrl)}
              />
              {formattedBadgeText && (
                <span className="absolute bottom-3 left-3 z-10 bg-[#00ADEF] text-white text-[12px] font-bold rounded-xl px-3 py-1.5 uppercase tracking-wide shadow-sm">
                  {formattedBadgeText}
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {/* Card Content Details */}
      <div className="flex flex-col items-start justify-start w-full px-4 pt-4 pb-2">
        <div className="flex items-center justify-between w-full mb-4">
          <div className="flex items-center gap-2 min-w-0">
            <img src={merchantLogo ? getS3PublicUrl(merchantLogo) : storeDefault} alt="" className="w-7 h-7 rounded-full object-cover bg-[#40E87D]" />
            <span className="text-[#20232D] font-poppins font-[600] text-[14px] truncate">{merchantName}</span>
          </div>
          <div className="flex items-center gap-1 text-[#20232D] text-[13px] font-[500]">
            <span className="text-[#FFA800]">★</span>
            <span>{rating ? `${rating} (2)` : "5.0 (2)"}</span>
          </div>
        </div>
        {offerTypeTag && (() => {
          // Tag text normalization
          const isOnline = offerData?.offerType === "ONLINE_OFFER" || offerTypeTag.toLowerCase().includes("online");
          const formattedTag = isOnline ? "Online" : "In-Store";

          return (
            <span className="inline-flex items-center gap-1.5 bg-[#F4F5F8] text-[#20232D] text-[12px] font-[500] rounded-full px-3 py-1 mb-4">
              {/* Existing components wrap in a blue wrapper */}
              <span className="text-[#00ADEF] flex items-center brightness-0 invert-0 [filter:invert(48%)_sepia(79%)_saturate(2476%)_hue-rotate(164deg)_brightness(101%)_contrast(101%)]">
                {isOnline ? <NewWebIcon /> : <NewInStoreIcon />}
              </span>
              {formattedTag}
            </span>
          );
        })()}

        {/* 1. Title */}
        <p className="font-poppins font-[700] text-[20px] text-[#222] mb-2 leading-tight break-words w-full">
          {offerData?.title}
        </p>

        <p
          className="font-poppins font-[400] text-[13px] text-[#767676] mb-3 break-words w-full leading-relaxed"
          style={{ wordBreak: "break-word", whiteSpace: "pre-line" }}
        >
          {String(offerData?.description || "").replace(/\r\n/g, "\n").replace(/\n{2,}/g, "\n")}
        </p>

        <div className="flex flex-wrap gap-2 mb-4">
          {offerData?.category?.title && (
            <span className="bg-[#F1F3F6] text-[#767676] text-[10px] font-[500] rounded-full px-2.5 py-1 flex items-center">
              {offerData?.category?.title}
            </span>
          )}
          {Array.isArray(offerData?.subCategory) && offerData?.subCategory.map((s, idx) => (
            <span key={idx} className="bg-[#F1F3F6] text-[#767676] text-[10px] font-[500] rounded-full px-2.5 py-1 flex items-center">
              {s?.title}
            </span>
          ))}
        </div>
        
        {/* Location / Address Section */}
        {(() => {
          const showAddress = offerData?.offerType === "ONLINE_OFFER" ? false : true;
          if (!showAddress) return null;
          const selectedOfferLocation = Array.isArray(offerData?.offerLocation)
            ? offerData.offerLocation[0]
            : offerData?.offerLocation;
          const selectedLocation =
            selectedOfferLocation?.location ||
            (typeof offerData?.locations?.[0] === "object"
              ? offerData.locations[0]
              : null) ||
            firstLocation ||
            location;
          const primaryAddress =
            selectedLocation?.googlePlaceId?.address || selectedLocation?.address;
          const distance = "2.2 Km Away"; // Placeholder for distance logic

          if (!primaryAddress) return null;

          return (
            <div className="flex items-start gap-2 mb-2">
              {/* Location Pin Icon */}
              <svg
                className="w-4 h-4 text-[#767676] shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.8"
                  d="M12.002 2.005c-3.866 0-7 3.134-7 7 0 5.25 7 13 7 13s7-7.75 7-13c0-3.866-3.134-7-7-7zm0 9.5a2.5 2.5 0 110-5 2.5 2.5 0 010 5z"
                />
              </svg>

              <div className="flex flex-col">
                <span className="text-[#20232D] text-[13px] font-[500] leading-tight">
                  {primaryAddress}
                </span>
                {distance && (
                  <span className="text-[#98A2B3] text-[11px] font-[400] mt-0.5">
                    {distance}
                  </span>
                )}
              </div>
            </div>
          );
        })()}

        {/* Validity Box */}
        {(() => {
          if (!offerData) return null;

          const { title, subtitle } = getRedemptionDetails({
            necessaryRule: offerData?.necessaryRule,
            frequency: offerData?.redemptionFrequency || offerData?.frequency,
            redemptionDays: offerData?.redemptionDays || offerData?.days || [],
            endDate: offerData?.endDate,
          });

          return (
            <div className="flex flex-col bg-[#E6F7FF] rounded-xl p-3 mb-4 w-full">
              <div className="flex items-center gap-1.5">
                <span className="text-[#00ADEF] text-[14px] font-[700]">
                  {title}
                </span>
              </div>

              {subtitle && (
                <span className="text-[#00ADEF] text-[12px] font-[500] mt-0.5">
                  {subtitle}
                </span>
              )}
            </div>
          );
        })()}

        {/* Rules & Regulations */}
        <div className="flex items-start gap-1 mb-1">

          <span className="text-[#20232D] text-[12px] font-[500]">Rules and regulations:</span>
        </div>
        <div className={`${offerData?.ruleAndRegulation?.length >= 3 ? 'rules-list-anim' : ''} ${showAllRules ? 'expanded' : ''} w-full`}>
          <ul className="list-disc pl-8 text-[#303030] text-[11px] mb-2 font-[400]">
            {offerData?.ruleAndRegulation?.length > 0 ? (
              (showAllRules
                ? offerData?.ruleAndRegulation
                : offerData?.ruleAndRegulation.slice(0, 2)
              ).map((rule, idx) => (
                <li key={idx} className="mb-1">{rule?.title}</li>
              ))
            ) : null}
          </ul>
        </div>

        {/* View More */}
        {offerData?.ruleAndRegulation?.length >= 3 && (
          <div className="w-full flex justify-start">
            <button
              className="text-[#767676] text-[12px] font-[500] flex items-center gap-1 mb-4 bg-transparent border-none p-0 cursor-pointer hover:underline"
              onClick={() => setShowAllRules(!showAllRules)}
            >
              {showAllRules ? "View Less" : "View More"}
              <span className="text-[14px]">{showAllRules ? " " : " >"}</span>
            </button>
          </div>
        )}
      </div>

      {/* Redeem Button - At the bottom of the inner card */}
      <div className="w-full px-4 mb-4 mt-auto">
        <button className="w-full bg-[#00ADEF] text-white font-poppins font-[600] text-[15px] rounded-[8px] py-3.5 mt-0 shadow-sm hover:bg-[#009bd6] transition-colors focus:outline-none">{redeemButtonLabel}</button>
      </div>
    </div>
  );
};

export default OfferDetails;
